// components/ScenarioDropdown.tsx — the login-screen story picker.
//
// Replaces the previous ScenarioPills row. The current scenario's heroTitle
// is rendered as a button; clicking opens a menu of all scenarios that the
// user can switch between. Click an entry → setSelected(id) → the entire
// login surface recolours via the existing theme machinery.
//
// Mirrors App.tsx's `TitleDropdown` (the in-game story switcher) so the
// pre-auth and post-auth pickers feel like siblings — same useHoverMenu
// hook, same chevron, same general structure. Differences:
//   - Source of truth is the ScenarioContext (ordered + selectedId), not
//     the user's stored stories.
//   - Menu is portalled to document.body via createPortal so it escapes
//     the login auth card's overflow / stacking context on small phones.
//   - Two `variant`s: "hero" (the big login title) and "compact" (the
//     smaller HeroOnlyPane title used on very narrow viewports).
//
// Hover/focus state machinery comes from useHoverMenu, same as TitleDropdown.

import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useScenario } from "../scenarios/ScenarioContext";
import { useHoverMenu } from "../hooks/useHoverMenu";

interface ScenarioDropdownProps {
  /** "hero" matches the large login title; "compact" is for HeroOnlyPane. */
  variant?: "hero" | "compact";
  /** Override the prefix label ("Choose:" by default). */
  label?: string;
}

interface Anchor {
  top: number;
  left: number;
  width: number;
}

export default function ScenarioDropdown({
  variant = "hero",
  label,
}: ScenarioDropdownProps) {
  const { ordered, selectedId, setSelected, effective } = useScenario();
  const menuRef = useRef<HTMLDivElement | null>(null);
  const { open, triggerProps, menuProps, wrapRef, close } = useHoverMenu({
    excludeRef: menuRef,
  });
  const { t } = useTranslation(["scenarios", "common"]);
  const heroTitle = t(`scenarios:${effective.id}.heroTitle`, {
    defaultValue: effective.pillLabel,
  });
  const chooseLabel = label ?? t("common:labels.choose", { defaultValue: "Choose:" });

  // Trigger button ref + anchor cache. We re-measure each time the menu
  // opens (and on scroll/resize while open) so the portal-rendered menu
  // tracks the trigger regardless of viewport gymnastics.
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [anchor, setAnchor] = useState<Anchor | null>(null);
  // Track which item the pointer is over so we can paint a hover tint —
  // selection itself only happens on click; this is purely visual feedback.
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !buttonRef.current) {
      setAnchor(null);
      return;
    }
    const measure = () => {
      const el = buttonRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setAnchor({ top: r.bottom + 6, left: r.left, width: r.width });
    };
    measure();
    // Close on outside-scroll/resize — the cached anchor would otherwise go
    // stale and the menu would drift away from the trigger. We listen in
    // capture phase because `scroll` doesn't bubble; that means scrolls on
    // inner elements (including the menu's own overflow-y scroll) fire too,
    // so we have to skip events whose target is inside the menu — otherwise
    // a single wheel tick on the menu would close it.
    const onScroll = (e: Event) => {
      const menuEl = menuRef.current;
      const target = e.target as Node | null;
      if (menuEl && target && menuEl.contains(target)) return;
      close();
    };
    const onResize = () => close();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open, close]);

  const isHero = variant === "hero";

  // Trigger styling — paints the title as a button. Visible chevron, hover
  // tint, and an obvious "open" state are the three cues that this is
  // interactive. On hover/focus a soft paper-edge tint appears behind the
  // title; when the menu is open the tint is stronger and the chevron
  // shifts colour, mirroring TitleDropdown's openState styling.
  const [hover, setHover] = useState(false);
  const [focus, setFocus] = useState(false);

  const triggerBase = isHero ? triggerStyleHero : triggerStyleCompact;
  const chevronBase = isHero ? chevronStyleHero : chevronStyleCompact;

  const triggerStateStyle: CSSProperties = open
    ? triggerOpenStyle
    : hover || focus
    ? triggerHoverStyle
    : {};

  const chevronStateStyle: CSSProperties = open
    ? chevronOpenStyle
    : {};

  return (
    <span ref={(el) => wrapRef(el)} style={wrapStyle}>
      {isHero && (
        <span style={chooseLabelStyleHero} aria-hidden>
          {chooseLabel}
        </span>
      )}
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("common:labels.switchScenario")}
        style={{ ...triggerBase, ...triggerStateStyle }}
        onMouseEnter={(e) => {
          setHover(true);
          triggerProps.onMouseEnter();
          void e;
        }}
        onMouseLeave={(e) => {
          setHover(false);
          triggerProps.onMouseLeave();
          void e;
        }}
        onFocus={(e) => {
          setFocus(true);
          triggerProps.onFocus(e);
        }}
        onBlur={(e) => {
          setFocus(false);
          triggerProps.onBlur(e);
        }}
        onClick={triggerProps.onClick}
      >
        <span style={triggerLabelStyle}>{heroTitle}</span>
        <span style={{ ...chevronBase, ...chevronStateStyle }} aria-hidden>
          ▾
        </span>
      </button>

      {open && anchor
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              tabIndex={-1}
              style={{
                ...menuStyle,
                top: anchor.top,
                left: anchor.left,
                minWidth: Math.max(anchor.width, 260),
              }}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  e.preventDefault();
                  close();
                  // Return focus to the trigger for keyboard users.
                  buttonRef.current?.focus();
                }
              }}
              {...menuProps}
            >
              {ordered.map((p) => {
                const isSelected = p.id === selectedId;
                const isHovered = hoveredId === p.id;
                const itemStyle: CSSProperties = isSelected
                  ? { ...menuItemStyle, ...menuItemCurrentStyle }
                  : isHovered
                  ? { ...menuItemStyle, ...menuItemHoverStyle }
                  : menuItemStyle;
                return (
                  <div
                    key={p.id}
                    role="menuitem"
                    tabIndex={isSelected ? -1 : 0}
                    style={itemStyle}
                    onMouseEnter={() => setHoveredId(p.id)}
                    onMouseLeave={() =>
                      setHoveredId((curr) => (curr === p.id ? null : curr))
                    }
                    onFocus={() => setHoveredId(p.id)}
                    onBlur={() =>
                      setHoveredId((curr) => (curr === p.id ? null : curr))
                    }
                    onClick={() => {
                      if (isSelected) {
                        close();
                        return;
                      }
                      setSelected(p.id);
                      close();
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        if (isSelected) {
                          close();
                          return;
                        }
                        setSelected(p.id);
                        close();
                      }
                    }}
                  >
                    <div style={menuItemNameStyle}>
                      {isSelected ? "▸ " : "  "}
                      {t(`scenarios:${p.id}.pillLabel`, { defaultValue: p.pillLabel })}
                    </div>
                    <div style={menuItemGenreStyle}>
                      {t(`scenarios:${p.id}.genre`, { defaultValue: p.genre })}
                    </div>
                  </div>
                );
              })}
            </div>,
            document.body,
          )
        : null}
    </span>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────
// Reads CSS variables set by the Login wrapper, matching the rest of the
// login surface. Falls back to the Ember Hollow palette when vars are
// missing — same fallback shape as styles.ts.

const wrapStyle: CSSProperties = {
  position: "relative",
  display: "flex",
  // Centre the "Choose:" label + dropdown trigger as one horizontal group.
  alignItems: "baseline",
  justifyContent: "center",
  flexWrap: "wrap",
  gap: "0.5em",
  width: "100%",
};

// "Choose:" eyebrow that sits to the left of the trigger title. Same size as
// the trigger so the two read as one phrase, but muted ink so it reads as an
// instruction rather than competing with the scenario name. Hero only.
const chooseLabelStyleHero: CSSProperties = {
  fontFamily: "'IM Fell English', 'Cormorant Garamond', Georgia, serif",
  fontSize: "2.4rem",
  fontWeight: 400,
  fontStyle: "italic",
  color: "var(--ink-muted, #a89070)",
  letterSpacing: "0.02em",
  textShadow: "0 0 24px rgba(0,0,0,0.4)",
  lineHeight: 1,
  // Selectable text — not a button. Keep cursor as text/default.
  cursor: "default",
};

const triggerStyleHero: CSSProperties = {
  margin: 0,
  marginBottom: 0,
  fontFamily: "'IM Fell English', 'Cormorant Garamond', Georgia, serif",
  fontSize: "2.4rem",
  fontWeight: 400,
  color: "var(--ink, #e8d9b8)",
  letterSpacing: "0.02em",
  textShadow: "0 0 24px rgba(0,0,0,0.4)",
  background: "transparent",
  border: "1px solid transparent",
  padding: "4px 14px",
  borderRadius: 4,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "baseline",
  justifyContent: "center",
  gap: 12,
  textAlign: "center",
  transition:
    "background 0.2s ease-out, border-color 0.2s ease-out, color 0.35s ease-out",
};

const triggerStyleCompact: CSSProperties = {
  margin: 0,
  fontFamily: "'IM Fell English', 'Cormorant Garamond', Georgia, serif",
  fontSize: "2.1rem",
  fontWeight: 400,
  color: "var(--ink, #e8d9b8)",
  letterSpacing: "0.02em",
  textShadow: "0 0 24px rgba(0,0,0,0.4)",
  background: "transparent",
  border: "1px solid transparent",
  padding: "4px 12px",
  borderRadius: 4,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "baseline",
  justifyContent: "center",
  gap: 10,
  textAlign: "center",
  transition:
    "background 0.2s ease-out, border-color 0.2s ease-out, color 0.35s ease-out",
};

const triggerLabelStyle: CSSProperties = {
  display: "inline-block",
};

// Hover/focus tint — subtle paper-edge box behind the title so it visibly
// reads as a button without competing with the title's typography.
const triggerHoverStyle: CSSProperties = {
  background: "var(--paper, rgba(20,14,10,0.55))",
  borderColor: "var(--paper-edge, rgba(212,165,116,0.18))",
};

// Open state — stronger tint, mirrors TitleDropdown's open colour shift.
const triggerOpenStyle: CSSProperties = {
  background: "var(--paper, rgba(20,14,10,0.7))",
  borderColor: "var(--accent-muted, rgba(212,165,116,0.45))",
  color: "var(--accent, #d4a574)",
};

const chevronStyleHero: CSSProperties = {
  fontSize: "1.3rem",
  lineHeight: 1,
  color: "var(--accent, #d4a574)",
  transform: "translateY(-2px)",
  transition: "color 0.2s ease-out, transform 0.2s ease-out",
};

const chevronStyleCompact: CSSProperties = {
  fontSize: "1.1rem",
  lineHeight: 1,
  color: "var(--accent, #d4a574)",
  transform: "translateY(-2px)",
  transition: "color 0.2s ease-out, transform 0.2s ease-out",
};

const chevronOpenStyle: CSSProperties = {
  color: "var(--ink, #e8d9b8)",
  transform: "translateY(-2px) rotate(180deg)",
};

// Menu — `position: fixed` because we portal to document.body. Top/left
// come from the trigger's getBoundingClientRect. Scrollable in case 11
// entries exceed the viewport height.
const menuStyle: CSSProperties = {
  position: "fixed",
  zIndex: 200,
  maxWidth: "min(360px, calc(100vw - 24px))",
  maxHeight: "min(420px, calc(100vh - 80px))",
  overflowY: "auto",
  padding: "6px 0",
  background: "var(--paper, rgba(20,14,10,0.97))",
  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",
  border: "1px solid var(--accent-muted, rgba(212,165,116,0.45))",
  borderRadius: 2,
  boxShadow: "0 8px 28px rgba(0,0,0,0.7)",
  color: "var(--ink, #e8d9b8)",
  fontFamily:
    "var(--font-body, 'Cormorant Garamond', 'EB Garamond', 'Iowan Old Style', 'Palatino Linotype', Palatino, Georgia, serif)",
  transition:
    "background 0.4s ease-out, border-color 0.4s ease-out, color 0.4s ease-out",
};

const menuItemStyle: CSSProperties = {
  padding: "10px 16px",
  cursor: "pointer",
  color: "var(--ink-muted, #a89070)",
  transition: "color 0.2s ease-out, background 0.2s ease-out",
  outline: "none",
};

const menuItemCurrentStyle: CSSProperties = {
  background: "rgba(212,165,116,0.06)",
  color: "var(--ink, #e8c896)",
  cursor: "default",
  boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.04)",
};

// Pointer-over highlight on non-selected items. Purely visual — selection
// only happens on click. Background tint is a touch stronger than the
// currently-selected row so the mouse target reads as "about to commit".
const menuItemHoverStyle: CSSProperties = {
  background: "rgba(212,165,116,0.14)",
  color: "var(--ink, #e8d9b8)",
};

const menuItemNameStyle: CSSProperties = {
  fontFamily:
    "var(--font-body, 'Cormorant Garamond', 'IM Fell English', Georgia, serif)",
  fontSize: "1.05rem",
  marginBottom: 2,
  lineHeight: 1.25,
};

// Genre subtitle — borrows the aesthetic of ScenarioPills' pillGenre row:
// monospace, letter-spaced, uppercase, muted ink.
const menuItemGenreStyle: CSSProperties = {
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.62rem",
  color: "var(--ink-muted, #7a6243)",
  textTransform: "uppercase",
  letterSpacing: "0.12em",
  lineHeight: 1.2,
};
